"use client";

/**
 * Simple Modal Component
 * 
 * A basic Modal component that's compatible with Modal.Stack.
 * This uses the simple Modal component (not compound components) so it can be used
 * with Mantine's Modal.Stack for proper stacking management.
 */

import * as React from "react";
import { Modal } from '@mantine/core';
import { cn } from "@/lib/utils.js";

export interface SimpleModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback when the modal should be closed */
  onClose: () => void;
  /** The title of the modal (string or custom node, e.g. icon + label) */
  title?: React.ReactNode;
  /** The content of the modal */
  children: React.ReactNode;
  /** Modal size */
  size?: string | number;
  /** Whether to center the modal */
  centered?: boolean;
  /** Whether to close on escape key */
  closeOnEscape?: boolean;
  /** Whether to close on click outside */
  closeOnClickOutside?: boolean;
  /** Whether to trap focus */
  trapFocus?: boolean;
  /** Whether to return focus */
  returnFocus?: boolean;
  /** Custom class name */
  className?: string;
  /** Stack ID for Modal.Stack */
  stackId?: string;
}

export const SimpleModal: React.FC<SimpleModalProps> = ({
  opened,
  onClose,
  title,
  children,
  size = "md",
  centered = true,
  closeOnEscape = true,
  closeOnClickOutside = true,
  trapFocus = true,
  returnFocus = true,
  className,
  stackId,
  ...props
}) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size={size}
      centered={centered}
      closeOnEscape={closeOnEscape}
      closeOnClickOutside={closeOnClickOutside}
      trapFocus={trapFocus}
      returnFocus={returnFocus}
      classNames={{
        // Paper: cap height and column layout so the body can scroll
        content: cn("max-h-[90vh] flex flex-col overflow-hidden", className),
        header: "flex-shrink-0",
        // Critical: min-h-0 lets flex children shrink; overflow-y-auto scrolls long forms
        body: "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain",
      }}
      {...props}
    >
      {children}
    </Modal>
  );
};
